/**
 * «Досье партнёра» в HUB (обращение FB-2, 31.10.2026): «Моё досье» (партнёр заполняет сам) и «Партнёры сети»
 * (карточки досье, открытые для партнёров). Бэкенд — `/api/wiki/dossier/*` (modules/partner-dossier). День рождения —
 * только день и месяц, возраста нет; в «Партнёры сети» нет ни семьи, ни достижений, ни контактов.
 *
 * Правило волн: lib/wikiApi.ts заморожен — новые вызовы лежат в своём модуле (клиент — `api` из '../api').
 */
import { api, ApiError } from '../api';

export interface DossierBirthday {
  day: number;
  month: number;
}

export interface DossierPhoto {
  id: string;
  caption: string | null;
}

/** Моё досье (полное — видит только сам партнёр и команда УК в CRM). */
export interface MyDossier {
  partnerId: string;
  partnerName: string;
  exists: boolean;
  birthday: DossierBirthday | null;
  birthdayLabel: string | null;
  /** День рождения из карточки партнёра в CRM (без года) — подсказка «подставить». */
  cardBirthday: DossierBirthday | null;
  hobbies: string;
  about: string;
  family: string;
  achievements: string;
  photos: DossierPhoto[];
  showToPartners: boolean;
  updatedAt: string | null;
}

export interface MyDossierPatch {
  birthday?: DossierBirthday | null;
  hobbies?: string;
  about?: string;
  family?: string;
  achievements?: string;
  showToPartners?: boolean;
}

/** Карточка «Партнёры сети». */
export interface NetworkCard {
  partnerId: string;
  name: string;
  cities: string[];
  birthday: DossierBirthday | null;
  birthdayLabel: string | null;
  /** Через сколько дней день рождения (0 — сегодня); null — не указан. */
  daysToBirthday: number | null;
  hobbies: string;
  about: string;
  photos: DossierPhoto[];
  isMine: boolean;
}

/** Те же пределы, что на бэкенде (`partner-dossier.rules.ts`). */
export const DOSSIER_LIMITS = { hobbies: 2000, about: 6000, family: 1500, achievements: 3000, caption: 120, photos: 6 } as const;
export const DOSSIER_PHOTO_MAX_MB = 15;

export const MONTHS_RU = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'] as const;

/** Дней в месяце для выбора дня рождения (февраль — 29: год не хранится). */
export const daysInBirthdayMonth = (month: number | null | undefined): number => [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][(month ?? 1) - 1] ?? 31;

/** «через 3 дня» / «завтра» / «сегодня» для плашки ближайшего дня рождения. */
export function birthdaySoonLabel(days: number): string {
  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  const mod10 = days % 10;
  const mod100 = days % 100;
  const word = mod10 === 1 && mod100 !== 11 ? 'день' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'дня' : 'дней';
  return `через ${days} ${word}`;
}

async function uploadPhoto(file: File, caption: string): Promise<MyDossier> {
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);
  const r = await fetch('/api/wiki/dossier/mine/photos', { method: 'POST', credentials: 'include', body: form });
  if (!r.ok) {
    let parsed: unknown = null;
    try {
      parsed = await r.json();
    } catch {
      /* не JSON */
    }
    const raw = parsed && typeof parsed === 'object' && 'message' in parsed ? (parsed as { message: unknown }).message : null;
    const msg = (Array.isArray(raw) ? raw.join('. ') : raw ? String(raw) : '') || (r.status === 429 ? 'Слишком много загрузок — попробуйте через несколько минут' : `HTTP ${r.status}`);
    throw new ApiError(r.status, parsed, msg);
  }
  return (await r.json()) as MyDossier;
}

export const dossierApi = {
  mine: () => api.get<MyDossier>('/wiki/dossier/mine'),
  save: (patch: MyDossierPatch) => api.patch<MyDossier>('/wiki/dossier/mine', patch),
  addPhoto: uploadPhoto,
  removePhoto: (photoId: string) => api.delete<MyDossier>(`/wiki/dossier/mine/photos/${encodeURIComponent(photoId)}`),
  network: () => api.get<NetworkCard[]>('/wiki/dossier/network'),
  /** Адрес картинки для <img>: тот же сайт, cookie сессии едет сама. */
  photoUrl: (partnerId: string, photoId: string, size: 'thumb' | 'full' = 'thumb'): string =>
    `/api/wiki/dossier/photos/${encodeURIComponent(partnerId)}/${encodeURIComponent(photoId)}?size=${size}`,
};
